import { doc, getDoc, setDoc, updateDoc, serverTimestamp, collection, addDoc, query, where, getDocs } from 'firebase/firestore';
import { db } from '../config/firebase';
import { computeCouponPeriod } from '../utils/couponPeriod';
import {
  UserSubscription, SubscriptionPlan, SalesRecord,
  SubscriptionEventType
} from '../types/subscription';
import { Capacitor } from '@capacitor/core';
import { stripeApi } from './stripeApiService';

export const getSubscription = async (userId: string): Promise<UserSubscription | null> => {
  try {
    const subscriptionRef = doc(db, 'users', userId, 'subscription', 'current');
    const subscriptionDoc = await getDoc(subscriptionRef);

    if (subscriptionDoc.exists()) {
      const data = subscriptionDoc.data();
      return {
        ...data,
        currentPeriodStart: data.currentPeriodStart?.toDate(),
        currentPeriodEnd: data.currentPeriodEnd?.toDate(),
        trialEnd: data.trialEnd?.toDate(),
        createdAt: data.createdAt?.toDate(),
        updatedAt: data.updatedAt?.toDate(),
      } as UserSubscription;
    }

    const now = new Date();

    const defaultSubscription: Omit<UserSubscription, 'createdAt' | 'updatedAt'> = {
      plan: 'free',
      status: 'active',
      currentPeriodStart: now,
    };

    await setDoc(subscriptionRef, {
      ...defaultSubscription,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    return {
      ...defaultSubscription,
      createdAt: new Date(),
      updatedAt: new Date(),
    };
  } catch (error) {
    console.error('Erro ao buscar assinatura:', error);
    return null;
  }
};

export const updateSubscription = async (
  userId: string,
  data: Partial<UserSubscription>
): Promise<void> => {
  try {
    const subscriptionRef = doc(db, 'users', userId, 'subscription', 'current');
    await updateDoc(subscriptionRef, {
      ...data,
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Erro ao atualizar assinatura:', error);
    throw error;
  }
};

export const cancelSubscription = async (userId: string): Promise<void> => {
  try {
    await updateSubscription(userId, {
      status: 'canceled',
      cancelAtPeriodEnd: true,
    });
  } catch (error) {
    console.error('Erro ao cancelar assinatura:', error);
    throw error;
  }
};

export const checkSubscriptionStatus = async (userId: string): Promise<boolean> => {
  try {
    // 1. Verificar primeiro no Firestore (Fonte da verdade local e overrides manuais)
    const subscription = await getSubscription(userId);
    const now = new Date();

    if (subscription) {
      // Se tivermos uma assinatura local válida e ativa (especialmente internal/manual)
      const isActiveStatus = subscription.status === 'active' || subscription.status === 'trialing';
      const isNotExpired = !subscription.currentPeriodEnd || subscription.currentPeriodEnd > now;

      // Se for override manual/interno ou apple, confiamos 100% no Firestore
      // Se for stripe, verificamos se não está expirado localmente, mas ainda validamos na API se possível 
      // (embora a API possa falhar, o local deve ter um buffer)
      if (isActiveStatus && isNotExpired) {
        // Se for internal, retornamos true direto
        if (subscription.provider === 'internal') {
          return true;
        }
      }
    }

    // 2. Se não for internal ou se estiver expirado/duvidoso, tentamos validar com Stripe
    // Isso é útil para manter sincronia se o webhook falhou
    try {
      const statusResponse = await stripeApi.getSubscriptionStatus(userId);
      if (statusResponse.success && statusResponse.data) {
        const { status } = statusResponse.data.status;
        const isActive = status === 'active' || status === 'trialing';
        return isActive;
      }
    } catch (apiError) {
      console.warn('Falha ao verificar status via API, usando cache local:', apiError);
    }

    // 3. Fallback final: Cache local do Firestore
    if (!subscription) return false;

    const isActive = subscription.status === 'active' || subscription.status === 'trialing';
    const notExpired = !subscription.currentPeriodEnd || subscription.currentPeriodEnd > now;

    return isActive && notExpired;
  } catch (error) {
    console.error('Erro ao verificar status da assinatura:', error);
    return false;
  }
};

export const redeemCoupon = async (userId: string, code: string): Promise<{ success: boolean; message: string }> => {
  try {
    // Buscar cupom
    const couponsRef = collection(db, 'internal_coupons');
    const q = query(couponsRef, where('code', '==', code.toUpperCase()), where('active', '==', true));
    const snapshot = await getDocs(q);

    if (snapshot.empty) {
      return { success: false, message: 'Cupom inválido ou expirado.' };
    }

    const couponDoc = snapshot.docs[0];
    const coupon = couponDoc.data() as any;

    // Validar limites de uso
    if (coupon.maxRedemptions && coupon.redemptions >= coupon.maxRedemptions) {
      return { success: false, message: 'Este cupom atingiu o limite de usos.' };
    }

    // Calcular novo período (sem mutar a data de início — ver couponPeriod)
    const { currentPeriodStart, currentPeriodEnd } = computeCouponPeriod(new Date(), coupon.durationInMonths || 1);

    // Atualizar assinatura do usuário
    const subscriptionRef = doc(db, 'users', userId, 'subscription', 'current');

    const newSubscriptionData = {
      plan: coupon.plan || 'premium',
      status: 'active',
      provider: 'internal',
      currentPeriodStart,
      currentPeriodEnd,
      updatedAt: serverTimestamp(),
      lastCouponUsed: code
    };

    await setDoc(subscriptionRef, newSubscriptionData, { merge: true });

    // Incrementar uso do cupom
    await updateDoc(couponDoc.ref, {
      redemptions: (coupon.redemptions || 0) + 1
    });

    return { success: true, message: `Cupom aplicado! Você agora é ${coupon.plan === 'pro' ? 'PRO' : 'Basic'}.` };

  } catch (error) {
    console.error('Erro ao resgatar cupom:', error);
    return { success: false, message: 'Erro ao processar cupom. Tente novamente.' };
  }
};

export const createCheckoutSession = async (
  userId: string,
  priceId: string,
  plan: SubscriptionPlan,
  userEmail: string,
  promotionCode?: string
): Promise<{ sessionId: string; url: string }> => {
  try {
    const result = await stripeApi.createCheckoutSession({
      priceId: priceId,
      successUrl: `${window.location.origin}/checkout/success?session_id={CHECKOUT_SESSION_ID}`,
      cancelUrl: `${window.location.origin}/pricing`,
      metadata: { userId, plan },
      email: userEmail,
      ...(promotionCode ? { promotionCode } : {})
    });

    if (!result.success || !result.data) {
      throw new Error(result.error || 'Erro ao criar sessão de checkout');
    }

    return {
      sessionId: result.data.session.id,
      url: result.data.session.url
    };
  } catch (error) {
    console.error('Erro ao criar checkout session');
    throw error;
  }
};

export const recordSubscriptionTransaction = async (record: SalesRecord) => {
  try {
    // Idempotency check
    const salesRef = collection(db, 'sales');
    let q;

    if (record.sessionId) {
      q = query(salesRef, where('sessionId', '==', record.sessionId));
    } else if (record.transactionId) {
      q = query(salesRef, where('transactionId', '==', record.transactionId));
    }

    if (q) {
      const snapshot = await getDocs(q);
      if (!snapshot.empty) {
        console.log('Transação já registrada');
        return;
      }
    }

    // Enrich data for analytics
    const now = new Date();
    const enrichedRecord = {
      ...record,
      createdAt: serverTimestamp(),
      week: `${now.getFullYear()}-W${getWeekNumber(now)}`,
      month: `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`,
      year: `${now.getFullYear()}`,
      timestamp: now.getTime(),
    };

    await addDoc(collection(db, 'sales'), enrichedRecord);
  } catch (error) {
    console.error('Erro ao registrar venda:', error);
    // Silent fail to not disrupt user flow
  }
};

export const logSubscriptionEvent = async (
  type: SubscriptionEventType,
  metadata: Record<string, any> = {},
  userId?: string
) => {
  try {
    const eventsRef = collection(db, 'subscription_events');
    const now = new Date();
    const deviceId = navigator.userAgent;

    await addDoc(eventsRef, {
      userId: userId || null,
      deviceId,
      type,
      metadata,
      timestamp: now.getTime(),
      createdAt: serverTimestamp(),
      platform: Capacitor.getPlatform(),
    });
  } catch (error) {
    console.error('Erro ao logar evento de assinatura:', error);
  }
};

function getWeekNumber(d: Date): number {
  d = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
  var yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  var weekNo = Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
  return weekNo;
}